import React, { useEffect, useState } from "react";
import { Smartphone, Download, X, Info } from "lucide-react";

const DISMISS_KEY = "pwa-install-dismissed";

/**
 * Reusable "Install on your phone" affordance for the PWA.
 *
 * Mounted on both Landing (pre-auth) and Dashboard (post-auth) — most repeat
 * users skip Landing because they're already signed in, so without the
 * post-auth mount the install prompt was effectively unreachable on mobile.
 *
 * Behaviour:
 *  - Hidden entirely if already installed (display-mode: standalone, or iOS
 *    Safari's navigator.standalone === true).
 *  - Hidden if previously dismissed (localStorage flag).
 *  - On Chromium-family browsers, listens for `beforeinstallprompt`, defers
 *    the native prompt, and triggers it from our own button (gives us
 *    consistent styling and avoids the cold-start dropoff).
 *  - On iOS Safari (no beforeinstallprompt support), the "Install" button
 *    opens a small step-by-step modal because there's no programmatic
 *    install API.
 */
export default function InstallAppBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    const isInstalled =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isInstalled) return; // never show inside an already-installed PWA

    if (!localStorage.getItem(DISMISS_KEY)) setShowBanner(true);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Imperative trigger: any page can dispatch this event (e.g. a marketing
    // CTA buried in hero copy) to re-open the install flow even after the
    // banner has been dismissed.
    const showFromEvent = () => {
      if (deferredPromptRef.current) {
        deferredPromptRef.current.prompt();
        deferredPromptRef.current.userChoice.then(({ outcome }) => {
          if (outcome === "accepted") {
            setShowBanner(false);
            localStorage.setItem(DISMISS_KEY, "true");
          }
          setDeferredPrompt(null);
        });
      } else {
        setShowGuide(true);
      }
    };
    window.addEventListener("pwa-show-install-guide", showFromEvent);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("pwa-show-install-guide", showFromEvent);
    };
  }, []);

  // Ref kept in sync with deferredPrompt so the event listener (which closes
  // over the initial value) always sees the latest beforeinstallprompt event.
  const deferredPromptRef = React.useRef(null);
  React.useEffect(() => { deferredPromptRef.current = deferredPrompt; }, [deferredPrompt]);

  const dismiss = () => {
    setShowBanner(false);
    localStorage.setItem(DISMISS_KEY, "true");
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setShowBanner(false);
        localStorage.setItem(DISMISS_KEY, "true");
      }
      setDeferredPrompt(null);
    } else {
      setShowGuide(true);
    }
  };

  if (!showBanner && !showGuide) return null;

  return (
    <>
      {showBanner && (
        <div
          className="relative w-full bg-ink text-white px-4 py-2.5 animate-slide-down"
          data-testid="install-app-banner"
        >
          <div className="container mx-auto flex items-center justify-between gap-3 max-w-5xl">
            <div className="flex items-center gap-2.5 text-[13px]">
              <Smartphone className="w-4 h-4 flex-shrink-0" />
              <span>
                Install <b>Golf Eclectic</b> on your phone for one-tap score entry.
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleInstall}
                className="bg-white text-ink px-3 py-1 rounded-md text-xs font-semibold hover:bg-surface-2 inline-flex items-center gap-1"
                data-testid="install-app-install-btn"
              >
                <Download className="w-3.5 h-3.5" /> Install
              </button>
              <button
                onClick={dismiss}
                className="p-1 hover:bg-white/10 rounded-md"
                data-testid="install-app-dismiss-btn"
                aria-label="Dismiss install prompt"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {showGuide && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
            data-testid="install-app-guide"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold tracking-tight text-ink flex items-center gap-2">
                <Info className="w-5 h-5 text-accent" /> Install on your phone
              </h3>
              <button
                onClick={() => setShowGuide(false)}
                className="p-1 hover:bg-app rounded-md"
                aria-label="Close install guide"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {isIOS ? (
              <ol className="space-y-2.5 text-sm text-ink-muted">
                <li className="flex gap-2.5">
                  <span className="text-accent font-semibold">1.</span> Tap the <b>Share</b> button at the bottom of Safari.
                </li>
                <li className="flex gap-2.5">
                  <span className="text-accent font-semibold">2.</span> Choose <b>Add to Home Screen</b>.
                </li>
                <li className="flex gap-2.5">
                  <span className="text-accent font-semibold">3.</span> Tap <b>Add</b>.
                </li>
              </ol>
            ) : (
              <ol className="space-y-2.5 text-sm text-ink-muted">
                <li className="flex gap-2.5">
                  <span className="text-accent font-semibold">1.</span> Open the Chrome menu (three dots).
                </li>
                <li className="flex gap-2.5">
                  <span className="text-accent font-semibold">2.</span> Tap <b>Install app</b> or <b>Add to Home screen</b>.
                </li>
                <li className="flex gap-2.5">
                  <span className="text-accent font-semibold">3.</span> Tap <b>Install</b>.
                </li>
              </ol>
            )}
            <button
              onClick={() => setShowGuide(false)}
              className="w-full mt-5 bg-ink hover:bg-ink-hover text-white py-2.5 rounded-lg font-medium text-sm"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
